import { useState, useEffect, useRef, useCallback } from 'react'
import { chatAPI, type AdminConversation, type ChatMessage } from '../../api/chat'
import { useAuthStore } from '../../store/authStore'
import { connectSocket, getSocket } from '../../socket/socketClient'

const GREEN = '#1D9E75'

const timeStr = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return "À l'instant"
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}j`
}

const Avatar = ({ name, size = 32 }: { name: string; size?: number }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: GREEN, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: size * 0.38, flexShrink: 0,
    fontFamily: 'DM Sans, sans-serif',
  }}>
    {name.charAt(0).toUpperCase()}
  </div>
)

export default function ChatPanel() {
  const { user } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [activeConv, setActiveConv] = useState<AdminConversation | null>(null)
  const [conversations, setConversations] = useState<AdminConversation[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [search, setSearch] = useState('')
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const totalUnread = conversations.reduce((sum, c) => sum + (c.adminUnread ?? 0), 0)

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
  }, [])

  // Charger la liste + rejoindre toutes les rooms socket
  useEffect(() => {
    if (!user || !['admin'].includes(user.role)) return

    const load = async () => {
      try {
        const convs = await chatAPI.listConversations()
        setConversations(convs)

        const socket = connectSocket()
        convs.forEach(c => socket.emit('join_conversation', c._id))
      } catch {
        // silent
      }
    }

    load()
  }, [user])

  // Listeners socket
  useEffect(() => {
    if (!user || !['admin'].includes(user.role)) return

    const socket = connectSocket()

    const handleNewMessage = (msg: ChatMessage) => {
      const convId = msg.conversationId

      // Mettre à jour la conversation dans la liste (si elle existe)
      setConversations(prev => {
        if (!prev.some(c => c._id === convId)) return prev
        const isActive = activeConv?._id === convId && isOpen
        return prev.map(c => {
          if (c._id !== convId) return c
          return {
            ...c,
            lastMessage: { content: msg.content, senderRole: msg.senderRole, createdAt: msg.createdAt },
            lastMessageAt: msg.createdAt,
            adminUnread: isActive ? 0 : (c.adminUnread ?? 0) + (msg.senderRole === 'livreur' ? 1 : 0),
          }
        })
      })

      // Ajouter au fil de messages seulement si c'est la conversation active
      if (activeConv?._id === convId) {
        setMessages(prev => {
          if (prev.some(m => m._id === msg._id)) return prev
          scrollToBottom()
          return [...prev, msg]
        })
        if (isOpen) {
          chatAPI.markRead(convId).catch(() => undefined)
        }
      }
    }

    const handleNewConversation = (conv: AdminConversation) => {
      setConversations(prev => {
        if (prev.some(c => c._id === conv._id)) return prev
        return [...prev, conv]
      })
      // Rejoindre la room pour recevoir les prochains messages
      socket.emit('join_conversation', conv._id)
    }

    const handleTyping = ({ conversationId }: { conversationId: string }) => {
      if (activeConv?._id === conversationId) {
        setIsTyping(true)
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000)
      }
    }

    socket.on('new_message', handleNewMessage)
    socket.on('new_conversation', handleNewConversation)
    socket.on('livreur_typing', handleTyping)

    return () => {
      socket.off('new_message', handleNewMessage)
      socket.off('new_conversation', handleNewConversation)
      socket.off('livreur_typing', handleTyping)
    }
  }, [user, activeConv, isOpen, scrollToBottom])

  const openConversation = async (conv: AdminConversation) => {
    setActiveConv(conv)
    setMessages([])
    setIsTyping(false)

    try {
      const { messages: hist } = await chatAPI.getMessages(conv._id)
      setMessages(hist)
      scrollToBottom()

      await chatAPI.markRead(conv._id)
      setConversations(prev =>
        prev.map(c => c._id === conv._id ? { ...c, adminUnread: 0 } : c)
      )
    } catch {
      // silent
    }
  }

  const sendMessage = async () => {
    if (!input.trim() || !activeConv || isSending) return
    const content = input.trim()
    setInput('')
    setIsSending(true)

    const optimistic: ChatMessage = {
      _id: `opt-${Date.now()}`,
      conversationId: activeConv._id,
      senderId: user?.id ?? '',
      senderRole: 'admin',
      senderName: user?.name ?? 'Admin',
      content,
      createdAt: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimistic])
    scrollToBottom()

    try {
      const socket = getSocket()
      if (socket?.connected) {
        socket.emit('send_message', { conversationId: activeConv._id, content })
        // Retirer l'optimiste — il sera remplacé par le broadcast new_message
        setMessages(prev => prev.filter(m => m._id !== optimistic._id))
      } else {
        const sent = await chatAPI.sendMessage(activeConv._id, content)
        setMessages(prev => prev.map(m => m._id === optimistic._id ? sent : m))
      }
    } catch {
      setMessages(prev => prev.filter(m => m._id !== optimistic._id))
    } finally {
      setIsSending(false)
    }
  }

  const handleTypingInput = (val: string) => {
    setInput(val)
    if (!activeConv) return
    const socket = getSocket()
    if (!socket?.connected) return
    socket.emit('admin_typing', { conversationId: activeConv._id })
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', { conversationId: activeConv._id })
    }, 1500)
  }

  const filtered = conversations.filter(c =>
    c.livreurName.toLowerCase().includes(search.toLowerCase())
  )

  if (!user || !['admin'].includes(user.role)) return null

  return (
    <>
      {/* Bubble flottante */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          title="Chat livreurs"
          style={{
            position: 'fixed', bottom: 28, right: 28, zIndex: 1000,
            width: 52, height: 52, borderRadius: '50%',
            background: GREEN, color: '#fff', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, boxShadow: '0 6px 24px rgba(29,158,117,0.45)',
            transition: 'transform 0.15s',
          }}
        >
          💬
          {totalUnread > 0 && (
            <span style={{
              position: 'absolute', top: -3, right: -3,
              background: '#ef4444', color: '#fff',
              borderRadius: '50%', width: 18, height: 18,
              fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'DM Sans, sans-serif',
            }}>
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 1000,
          width: 340, height: 520,
          background: '#fff', borderRadius: 16,
          border: '1px solid #e5e7eb',
          boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', fontFamily: 'DM Sans, sans-serif',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
            background: '#fff', gap: 8,
          }}>
            {activeConv ? (
              <>
                <button
                  onClick={() => { setActiveConv(null); setMessages([]) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280', fontSize: 18 }}
                >←</button>
                <Avatar name={activeConv.livreurName} size={30} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827', flex: 1 }}>
                  {activeConv.livreurName}
                </span>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827', flex: 1 }}>
                  Messages
                  {totalUnread > 0 && (
                    <span style={{ color: GREEN, marginLeft: 4 }}>({totalUnread})</span>
                  )}
                </span>
              </>
            )}
            <button
              onClick={() => { setIsOpen(false); setActiveConv(null); setMessages([]) }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}
            >✕</button>
          </div>

          {/* Vue liste */}
          {!activeConv && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* Recherche */}
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #f9fafb' }}>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un livreur..."
                  style={{
                    width: '100%', padding: '7px 12px', border: '1px solid #e5e7eb',
                    borderRadius: 8, fontSize: 13, outline: 'none',
                    background: '#f9fafb', color: '#111827', boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Liste des conversations */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Aucune conversation
                  </div>
                ) : (
                  filtered
                    .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
                    .map(conv => (
                      <button
                        key={conv._id}
                        onClick={() => openConversation(conv)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center',
                          gap: 10, padding: '10px 14px', border: 'none',
                          background: conv.adminUnread > 0 ? '#f0fdf4' : '#fff',
                          cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                          textAlign: 'left',
                        }}
                      >
                        <Avatar name={conv.livreurName} size={36} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                              {conv.livreurName}
                            </span>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>
                              {timeAgo(conv.lastMessageAt)}
                            </span>
                          </div>
                          <div style={{
                            fontSize: 12, color: '#6b7280',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {conv.lastMessage
                              ? `${conv.lastMessage.senderRole === 'admin' ? 'Vous : ' : ''}${conv.lastMessage.content}`
                              : 'Pas encore de message'
                            }
                          </div>
                        </div>
                        {(conv.adminUnread ?? 0) > 0 && (
                          <span style={{
                            background: GREEN, color: '#fff', borderRadius: '50%',
                            width: 18, height: 18, fontSize: 10, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                            {conv.adminUnread > 9 ? '9+' : conv.adminUnread}
                          </span>
                        )}
                      </button>
                    ))
                )}
              </div>
            </div>
          )}

          {/* Vue conversation */}
          {activeConv && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {messages.map(msg => {
                  const isAdmin = msg.senderRole === 'admin'
                  return (
                    <div key={msg._id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '75%',
                        padding: '8px 12px',
                        borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: isAdmin ? GREEN : '#f3f4f6',
                        color: isAdmin ? '#fff' : '#1f2937',
                        fontSize: 13, lineHeight: 1.45,
                      }}>
                        <div>{msg.content}</div>
                        <div style={{ fontSize: 10, opacity: 0.65, marginTop: 3, textAlign: 'right' }}>
                          {timeStr(msg.createdAt)}
                        </div>
                      </div>
                    </div>
                  )
                })}
                {isTyping && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 2px' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{
                          width: 6, height: 6, borderRadius: '50%', background: '#9ca3af',
                          animation: `bounce 1.2s ${i * 0.2}s infinite`,
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>est en train d'écrire…</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={{
                padding: '10px 12px', borderTop: '1px solid #f0f0f0',
                display: 'flex', gap: 8, alignItems: 'flex-end',
              }}>
                <textarea
                  value={input}
                  onChange={e => handleTypingInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
                  placeholder="Écrire un message…"
                  rows={1}
                  style={{
                    flex: 1, resize: 'none', border: '1px solid #e5e7eb',
                    borderRadius: 10, padding: '8px 12px', fontSize: 13,
                    outline: 'none', fontFamily: 'inherit', color: '#111827',
                    maxHeight: 80, overflowY: 'auto', background: '#f9fafb',
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isSending}
                  style={{
                    width: 36, height: 36, borderRadius: '50%', border: 'none',
                    background: input.trim() ? GREEN : '#e5e7eb',
                    color: '#fff', cursor: input.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0, transition: 'background 0.15s',
                  }}
                >➤</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Keyframes pour le typing indicator */}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </>
  )
}
